import React, { useEffect, useState } from 'react'
import {
  Box,
  Collapse,
  Drawer,
  Typography,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  PlayArrow as DemoIcon,
  CheckCircle as BilledIcon,
  Cancel as NotBilledIcon,
  Warning as ExpiredIcon,
  Schedule as TrainingIcon,
  AccountTree as SopIcon,
  Campaign as AnnouncementIcon,
  AttachMoney as RevenueIcon,
  Assessment as AnalyticsIcon,
  Inventory2 as ProductIcon,
  Search as SearchIcon,
  Description as ContractsIcon,
  Refresh as RenewalsIcon,
  ContactSupport as SupportIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  AccountCircle as AccountCircleIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  Group as GroupIcon,
  Phone as PhoneIcon,
  SmartToy as AgentIcon,
  People as ReferralIcon,
  RecordVoiceOver as PromoterIcon,
  FactCheck as ValidationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as BillDeskIcon,
  Language as WebsiteIcon,
  ReceiptLong as TransactionPanelIcon,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { APP_HEADER_HEIGHT } from './layoutConstants'
import { NAV_CHROME as N } from './navChrome'

const drawerWidth = N.drawerWidth

const getDashboardPath = (userRole) => {
  if (userRole === 'TL') return '/dashboard/tl'
  if (userRole === 'TRAINER') return '/dashboard/trainer'
  return '/dashboard'
}

const isPathActive = (pathname, path, { exact = false } = {}) => {
  if (!path) return false
  if (
    exact ||
    path === '/dashboard' ||
    path === '/dashboard/tl' ||
    path === '/dashboard/trainer'
  ) {
    return pathname === path
  }
  if (path === '/schools') {
    if (pathname === '/schools') return true
    if (!pathname.startsWith('/schools/')) return false
    const otherSchoolLists = [
      '/schools/search',
      '/schools/demo',
      '/schools/billed',
      '/schools/not-billed',
      '/schools/expired',
    ]
    return !otherSchoolLists.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`)
    )
  }
  return pathname === path || pathname.startsWith(`${path}/`)
}

const isItemActive = (pathname, item) => {
  if (item.children?.length) {
    return item.children.some((child) =>
      isPathActive(pathname, child.path, { exact: child.path !== '/schools' })
    )
  }
  return isPathActive(pathname, item.path)
}

const getMenuGroups = (userRole) => {
  const dash = getDashboardPath(userRole)

  const main = [
    { text: 'Dashboard', icon: <DashboardIcon sx={{ fontSize: 13 }} />, path: dash, roles: ['ADMIN', 'TL', 'TRAINER'] },
    {
      text: 'My School',
      icon: <SchoolIcon sx={{ fontSize: 13 }} />,
      roles: ['ADMIN', 'TL', 'TRAINER'],
      children: [
        { text: 'Search School', icon: <SearchIcon sx={{ fontSize: 12 }} />, path: '/schools/search', roles: ['ADMIN', 'TL', 'TRAINER'] },
        { text: 'All Schools', icon: <SchoolIcon sx={{ fontSize: 12 }} />, path: '/schools', roles: ['ADMIN', 'TL', 'TRAINER'] },
        { text: 'Demo Schools', icon: <DemoIcon sx={{ fontSize: 12 }} />, path: '/schools/demo', roles: ['ADMIN', 'TL'] },
        { text: 'Billed Schools', icon: <BilledIcon sx={{ fontSize: 12 }} />, path: '/schools/billed', roles: ['ADMIN', 'TL'] },
        { text: 'Not Billed', icon: <NotBilledIcon sx={{ fontSize: 12 }} />, path: '/schools/not-billed', roles: ['ADMIN', 'TL'] },
        { text: 'Expired Schools', icon: <ExpiredIcon sx={{ fontSize: 12 }} />, path: '/schools/expired', roles: ['ADMIN', 'TL'] },
      ],
    },
    { text: 'Training', icon: <TrainingIcon sx={{ fontSize: 13 }} />, path: '/training', roles: ['ADMIN', 'TL', 'TRAINER'] },
    { text: 'SOP', icon: <SopIcon sx={{ fontSize: 13 }} />, path: '/sop', roles: ['ADMIN', 'TL', 'TRAINER'] },
    { text: 'Announcements', icon: <AnnouncementIcon sx={{ fontSize: 13 }} />, path: '/announcements', roles: ['ADMIN', 'TL'] },
    { text: 'Product List', icon: <ProductIcon sx={{ fontSize: 13 }} />, path: '/product-list', roles: ['ADMIN', 'TL'] },
  ]

  const business = [
    { text: 'BillDesk', icon: <BillDeskIcon sx={{ fontSize: 13 }} />, path: '/billdesk', roles: ['ADMIN', 'TL'] },
    { text: 'Renewals', icon: <RenewalsIcon sx={{ fontSize: 13 }} />, path: '/renewals', roles: ['ADMIN'] },
    { text: 'Transaction Panel', icon: <TransactionPanelIcon sx={{ fontSize: 13 }} />, path: '/transaction-panel', roles: ['ADMIN', 'TL'] },
    { text: 'Revenue', icon: <RevenueIcon sx={{ fontSize: 13 }} />, path: '/billdesk', roles: ['ADMIN'] },
    { text: 'Analytics', icon: <AnalyticsIcon sx={{ fontSize: 13 }} />, path: '/analytics', roles: ['ADMIN'] },
    { text: 'Contracts', icon: <ContractsIcon sx={{ fontSize: 13 }} />, path: '/contracts', roles: ['ADMIN'] },
  ]

  const messaging = [
    { text: 'Support Tickets', icon: <SupportIcon sx={{ fontSize: 13 }} />, path: '/support', roles: ['ADMIN', 'TL'] },
    { text: 'Support Chat', icon: <HelpIcon sx={{ fontSize: 13 }} />, path: '/support-chat', roles: ['ADMIN', 'TL'] },
    { text: 'WhatsApp Messaging', icon: <WhatsAppIcon sx={{ fontSize: 13 }} />, path: '/whatsapp', roles: ['ADMIN', 'TL'] },
    { text: 'WhatsApp Accounts', icon: <WhatsAppIcon sx={{ fontSize: 13 }} />, path: '/whatsapp/accounts', roles: ['ADMIN', 'TL'] },
    { text: 'School Query', icon: <HelpIcon sx={{ fontSize: 13 }} />, path: '/school-query', roles: ['ADMIN', 'TL'] },
  ]

  const operations = [
    { text: 'School Accounts', icon: <AccountCircleIcon sx={{ fontSize: 13 }} />, path: '/school-accounts', roles: ['ADMIN'] },
    { text: 'Login Logs', icon: <HistoryIcon sx={{ fontSize: 13 }} />, path: '/login-logs', roles: ['ADMIN'] },
    { text: 'School Groups', icon: <GroupIcon sx={{ fontSize: 13 }} />, path: '/school-groups', roles: ['ADMIN', 'TL'] },
    { text: 'Telecaller Management', icon: <PhoneIcon sx={{ fontSize: 13 }} />, path: '/telecaller', roles: ['ADMIN', 'TL'] },
    { text: 'AI Agents', icon: <AgentIcon sx={{ fontSize: 13 }} />, path: '/ai-agents', roles: ['ADMIN'] },
    { text: 'Referral Management', icon: <ReferralIcon sx={{ fontSize: 13 }} />, path: '/referrals', roles: ['ADMIN', 'TL'] },
    { text: 'Promoter Management', icon: <PromoterIcon sx={{ fontSize: 13 }} />, path: '/promoters', roles: ['ADMIN', 'TL'] },
    { text: 'Validation', icon: <ValidationIcon sx={{ fontSize: 13 }} />, path: '/validation', roles: ['ADMIN', 'TL'] },
    { text: 'Manage Website', icon: <WebsiteIcon sx={{ fontSize: 13 }} />, path: '/manage-website', roles: ['ADMIN', 'TL'] },
  ]

  const settings = [
    { text: 'Settings', icon: <SettingsIcon sx={{ fontSize: 13 }} />, path: '/settings', roles: ['ADMIN', 'TL', 'TRAINER'] },
  ]

  const filter = (items) =>
    items
      .filter((item) => item.roles.includes(userRole))
      .map((item) => {
        if (!item.children?.length) return item
        const children = item.children.filter((child) => child.roles.includes(userRole))
        if (!children.length) return null
        return { ...item, children }
      })
      .filter(Boolean)

  return [
    { label: null, items: filter(main) },
    { label: 'Business & Finance', items: filter(business) },
    { label: 'Messaging & Support', items: filter(messaging) },
    { label: 'Operations', items: filter(operations) },
    { label: 'System', items: filter(settings) },
  ].filter((g) => g.items.length > 0)
}

const navButtonSx = (active, { nested = false } = {}) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: nested ? 0.75 : 1,
  px: nested ? '8px' : '9px',
  py: nested ? '6px' : '7px',
  borderRadius: nested ? '12px' : '14px',
  border: `1px solid ${active ? N.yellowBorder : N.borderSoft}`,
  background: active
    ? `linear-gradient(135deg, ${N.yellowSoft} 0%, ${N.yellowSofter} 100%)`
    : N.glass,
  color: active ? N.yellow : N.sidebarText,
  fontWeight: active ? 650 : 500,
  fontSize: nested ? '13px' : '15px',
  fontFamily: '"Open Sans", Arial, sans-serif',
  textAlign: 'left',
  cursor: 'pointer',
  backdropFilter: 'blur(8px)',
  boxShadow: active ? '0 4px 12px rgba(255,179,0,0.16)' : 'none',
  transition: 'transform 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
  whiteSpace: 'nowrap',
  minWidth: 0,
  '&:hover': {
    transform: 'translateX(2px)',
    background: active
      ? `linear-gradient(135deg, ${N.yellowSoft} 0%, ${N.yellowSofter} 100%)`
      : N.glassHover,
    borderColor: active ? N.yellowBorder : N.glassBorder,
    color: active ? N.yellow : N.sidebarText,
  },
})

const NavIconBox = ({ active, iconColor, size = 28, children }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: size <= 24 ? '8px' : '9px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.88)',
      color: iconColor,
      border: active
        ? '1px solid rgba(255,179,0,0.65)'
        : '1px solid rgba(255,255,255,0.55)',
      boxShadow: active
        ? '0 2px 8px rgba(255,179,0,0.28)'
        : '0 1px 3px rgba(0,0,0,0.12)',
    }}
  >
    {children}
  </Box>
)

/** Flat index across groups so icon colors cycle like EduOKWeb */
const NavItem = ({ item, active, onClick, colorIndex, nested = false, endAdornment = null }) => {
  const iconColor = active
    ? N.yellowDeep
    : N.iconColors[colorIndex % N.iconColors.length]

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={navButtonSx(active, { nested })}
    >
      <NavIconBox active={active} iconColor={iconColor} size={nested ? 24 : 28}>
        {item.icon}
      </NavIconBox>
      <Typography
        component="span"
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 'inherit',
          fontWeight: 'inherit',
          color: 'inherit',
          lineHeight: 1.25,
        }}
      >
        {item.text}
      </Typography>
      {endAdornment}
    </Box>
  )
}

const NavParentItem = ({ item, pathname, onNavigate, colorIndex }) => {
  const childActive = isItemActive(pathname, item)
  const [open, setOpen] = useState(childActive)

  useEffect(() => {
    if (childActive) setOpen(true)
  }, [childActive, pathname])

  const iconColor = childActive
    ? N.yellowDeep
    : N.iconColors[colorIndex % N.iconColors.length]

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        sx={navButtonSx(childActive)}
      >
        <NavIconBox active={childActive} iconColor={iconColor}>
          {item.icon}
        </NavIconBox>
        <Typography
          component="span"
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
            lineHeight: 1.25,
          }}
        >
          {item.text}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            color: childActive ? N.yellow : N.sidebarTextMuted,
            flexShrink: 0,
          }}
        >
          {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </Box>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Stack spacing={0.5} sx={{ mt: 0.5, ml: 1.25, pl: 1, borderLeft: `1px solid ${N.borderSoft}` }}>
          {item.children.map((child, i) => (
            <NavItem
              key={`${child.text}-${child.path}`}
              item={child}
              active={isPathActive(pathname, child.path, {
                exact: child.path !== '/schools',
              })}
              onClick={() => onNavigate(child.path)}
              colorIndex={colorIndex + i + 1}
              nested
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  )
}

const NavGroup = ({ label, items, pathname, onNavigate, startIndex }) => {
  if (!items?.length) return null
  return (
    <Stack spacing={0.75} sx={{ mt: label ? 1.25 : 0, '&:first-of-type': { mt: 0 } }}>
      {label && (
        <Typography
          sx={{
            mx: '2px',
            mt: 1.25,
            mb: 0.25,
            px: 0.75,
            fontSize: '8.5px',
            fontWeight: 750,
            fontFamily: '"Open Sans", Arial, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: N.sidebarLabel,
            opacity: 0.95,
          }}
        >
          {label}
        </Typography>
      )}
      {items.map((item, i) =>
        item.children?.length ? (
          <NavParentItem
            key={item.text}
            item={item}
            pathname={pathname}
            onNavigate={onNavigate}
            colorIndex={startIndex + i}
          />
        ) : (
          <NavItem
            key={`${item.text}-${item.path}`}
            item={item}
            active={isPathActive(pathname, item.path)}
            onClick={() => onNavigate(item.path)}
            colorIndex={startIndex + i}
          />
        )
      )}
    </Stack>
  )
}

const Sidebar = ({ open, onToggle }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const { getUserRole } = useAuth()

  const userRole = getUserRole() || 'ADMIN'
  const groups = getMenuGroups(userRole)

  const handleNavigation = (path) => {
    navigate(path)
    if (isMobile) onToggle()
  }

  let runningIndex = 0

  const drawerContent = (
    <Box
      className="sidebar-nav"
      sx={{
        position: 'relative',
        zIndex: 1,
        p: '10px 8px',
        minHeight: 'min-content',
      }}
    >
      {isMobile && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5, px: 0.5 }}
        >
          <Typography sx={{ fontWeight: 700, color: N.sidebarText, fontSize: '0.95rem' }}>
            Menu
          </Typography>
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label="Close menu"
            sx={{
              color: N.sidebarText,
              bgcolor: N.glass,
              border: `1px solid ${N.glassBorder}`,
              borderRadius: '10px',
              '&:hover': { bgcolor: N.glassHover, color: N.yellow },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}

      {groups.map((group) => {
        const startIndex = runningIndex
        runningIndex += group.items.length
        return (
          <NavGroup
            key={group.label || 'main'}
            label={group.label}
            items={group.items}
            pathname={location.pathname}
            onNavigate={handleNavigation}
            startIndex={startIndex}
          />
        )
      })}
    </Box>
  )

  const paperSx = {
    width: drawerWidth,
    boxSizing: 'border-box',
    borderRight: `1px solid ${N.border}`,
    background: N.sidebarGradient,
    color: N.sidebarText,
    fontFamily: '"Open Sans", Arial, sans-serif',
    overflowY: 'auto',
    boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.04)',
  }

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            ...paperSx,
            top: `${APP_HEADER_HEIGHT}px`,
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            height: `calc(100dvh - ${APP_HEADER_HEIGHT}px)`,
            maxWidth: 'min(280px, 85vw)',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,20,18,0.5)',
            backdropFilter: 'blur(2px)',
            top: `${APP_HEADER_HEIGHT}px`,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    )
  }

  return (
    <Box
      component="nav"
      className={`app-sidebar${open ? '' : ' app-sidebar--collapsed'}`}
      aria-hidden={!open}
      sx={{
        width: open ? drawerWidth : 0,
        borderRight: open ? `1px solid ${N.border}` : 'none',
        background: N.sidebarGradient,
        color: N.sidebarText,
        fontFamily: '"Open Sans", Arial, sans-serif',
        transition: 'width 0.2s ease',
        position: 'relative',
        overflowY: open ? 'auto' : 'hidden',
        overflowX: 'hidden',
        '&::before': open
          ? {
              content: '""',
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'radial-gradient(ellipse 90% 40% at 0% 0%, rgba(3,169,244,0.12) 0%, transparent 55%)',
              zIndex: 0,
            }
          : undefined,
      }}
    >
      <Box sx={{ width: drawerWidth, position: 'relative', zIndex: 1 }}>{drawerContent}</Box>
    </Box>
  )
}

export default Sidebar
